import { orderMetricsApi } from 'api/orderMetrics'
import {
  DateRangeFilterType,
  getDateFilter,
} from 'components/DateFilter/utilities'
import useCurrentMerchant from 'hooks/useCurrentMerchant'
import moment from 'moment-timezone'
import { useEffect, useMemo, useState } from 'react'
import { useAbility } from 'utilities/ability'

// The promo page's ROI band is framed as "based on your data last 30 days", so
// the order volume behind it reads the same window as the Purchases report.
const RANGE: DateRangeFilterType = 'last_30'
/**
 * How long a single request attempt is allowed to sit in `isLoading` before
 * this hook gives up on it and reports the unavailable `null` instead. A
 * hung connection (gateway 5xx, a stalled connection that never completes)
 * can otherwise sit in `isLoading` for as long as the browser/ALB is willing
 * to wait — 30-60s, far past the point a merchant should still be looking at
 * a loading state on a marketing page. This does NOT abort the request: if
 * it resolves later anyway, `data`/`isError` update normally on the next
 * render and the section re-rolls onto the real (now late) figures.
 */
export const LOADING_CEILING_MS = 15_000

export type MonthlyOrders = {
  /**
   * Total orders in the last 30 days. A successful response always yields the
   * real number, `0` included — a merchant with no orders in the window (the
   * endpoint answers with an empty series) genuinely has zero, and this hook
   * reports that truthfully rather than laundering it into `null`. `null`
   * means the figure does not exist yet: no merchant, request in flight, or
   * the request errored. RoiSection, the only consumer today, shows a real
   * zero exactly like any other live figure ("+$0/month" and all) — it's the
   * true number, not a broken one — and reserves `null` for when there is
   * genuinely nothing to show.
   */
  monthlyOrders: number | null
  /**
   * The merchant's measured average check for the same window, in integer
   * cents. `null` when the backend has no orders to average (or the figure is
   * not available yet) — callers fall back to the product's default check.
   */
  averageCheckCents: number | null
  /**
   * Real 👎 rate = negativeRatingCount ÷ monthlyOrders (D1/D9). `null` ⇔ NOT
   * computable: `monthlyOrders` is 0 or not yet known, the BE field is absent
   * (not yet shipped), the metric is unavailable (no merchant / no reporting
   * access / error / loading-ceiling), or still loading. A genuine zero-👎
   * merchant with orders > 0 yields `0` (NOT null).
   */
  complaintRate: number | null
  /**
   * Raw 30d `OrderRating.down` count from the BE (D9/D14). `null` when the
   * field is absent (BE not shipped) or the metric is unavailable/loading.
   * `0` is a real, computable zero — this is the signal the ROI-visibility
   * gate keys its zero-👎 hide on.
   */
  negativeRatingCount: number | null
  /**
   * `true` only while genuinely waiting on a request this hook still expects
   * to resolve soon. Two things beside a normal in-flight request also drive
   * this back to `false` with the figures still `null`: the request erroring
   * (`isError`, any status — a 403 here is expected to be rare given the
   * `canViewReports` skip below, and even then baseQuery is told not to toast
   * it, see orderMetrics.ts), and LOADING_CEILING_MS elapsing on a request
   * that's simply taking too long (a hung connection). Neither is
   * distinguishable from the caller's side — both just mean "stop showing a
   * loading state, there is no live figure coming soon" — so both report
   * through the same `isLoading: false, monthlyOrders: null` shape.
   */
  isLoading: boolean
}

const NO_FIGURES = {
  averageCheckCents: null,
  complaintRate: null,
  monthlyOrders: null,
  negativeRatingCount: null,
}

/**
 * Real 30-day order volume and average check for the current merchant, from the
 * merchant-api `order_metrics` resource (a single count plus average check).
 */
const useMonthlyOrders = (): MonthlyOrders => {
  const merchant = useCurrentMerchant()
  const merchantId: number | undefined = merchant?.id
  const timezone = useMemo(() => moment.tz.guess(), [])
  const { range, start, end } = useMemo(
    () => getDateFilter(RANGE, timezone),
    [timezone]
  )

  // `order_metrics` is `rbac_authorize!('view', 'reporting')` server-side, which
  // maps to `access` on the `Reports` CASL subject (permissionMap.ts: reporting →
  // [['access', 'Reports']]). Without it the request would 403 -- skipping
  // avoids sending it at all, on a page open to every authenticated merchant
  // user, and yields the documented monthlyOrders/averageCheckCents `null`
  // path so the ROI band simply shows the dash placeholder. The endpoint
  // also opts out of baseQuery's RBAC danger toast (orderMetrics.ts,
  // extraOptions.skipErrorToast) as a second line of defense, in case this
  // client-side check and the server's ever disagree.
  const canViewReports = useAbility().can('access', 'Reports')

  // The merchant arrives asynchronously, and the request is scoped to it by the
  // base URL — without one there is nothing to fetch.
  const {
    data,
    isError,
    isLoading: queryIsLoading,
    requestId,
  } = orderMetricsApi.useGetOrderMetricsQuery(
    { range, queryStartDate: start, queryEndDate: end },
    { skip: !merchantId || !canViewReports }
  )

  // Give-up ceiling for a hung request: `requestId` changes every time RTK
  // Query starts a new attempt (a fresh merchant, a refetch, ...), so keying
  // the effect on it re-arms a full LOADING_CEILING_MS for each one rather
  // than accumulating time across attempts. Not tied to any loading-state
  // animation on the consuming side (e.g. a spinner) at all — that keeps
  // running for however long `isLoading` says to; this is strictly about
  // how long `isLoading` itself is allowed to stay `true`.
  const [ceilingReached, setCeilingReached] = useState(false)
  useEffect(() => {
    setCeilingReached(false)
    if (!queryIsLoading) return

    const id = setTimeout(() => setCeilingReached(true), LOADING_CEILING_MS)
    return () => clearTimeout(id)
  }, [queryIsLoading, requestId])

  return useMemo(() => {
    if (!merchantId || !canViewReports) {
      return { ...NO_FIGURES, isLoading: false }
    }

    if (isError) return { ...NO_FIGURES, isLoading: false }

    if (!data) {
      return { ...NO_FIGURES, isLoading: !ceilingReached }
    }

    // Real rate only when there's a real denominator AND a real count — an
    // absent count (BE not shipped / unavailable) must not launder into 0.
    const negativeRatingCount = data.negativeRatingCount
    const complaintRate =
      data.ordersCount > 0 && negativeRatingCount !== null
        ? negativeRatingCount / data.ordersCount
        : null

    return {
      averageCheckCents: data.averageCheckCents,
      complaintRate,
      monthlyOrders: data.ordersCount,
      negativeRatingCount,
      isLoading: false,
    }
  }, [canViewReports, ceilingReached, data, isError, merchantId])
}

export default useMonthlyOrders
